import type { NewickNode } from "@/lib/tool-kit/ecology/newick";

/**
 * Rectangular tree layout.
 *
 * Tips are spaced evenly down the page in order; an internal node sits at the midpoint
 * of its first and last child. Horizontal position is either:
 *
 * - phylogram: the summed branch length from the root, so distances mean something and
 *   tips end at different places unless the tree is ultrametric;
 * - cladogram: branch lengths ignored. A node's x is the longest chain of branches
 *   below it counted back from the tips, so every tip ends at the same x and a parent
 *   is always left of its children.
 */

export type TreeStyle = "phylogram" | "cladogram";
export type CladeOrder = "given" | "ascending" | "descending";

export interface LaidOutNode {
  id: number;
  parent: number | null;
  /** Number of branches between this node and the root. */
  depth: number;
  name: string | null;
  length: number | null;
  support: string | null;
  isLeaf: boolean;
  leafCount: number;
  /** Phylogram: distance from the root. Cladogram: levels from the root. */
  x: number;
  /** Row position: tips are 0, 1, 2 … and internal nodes fall between. */
  y: number;
  children: number[];
}

export interface TreeLayout {
  nodes: LaidOutNode[];
  /** Node ids of the tips, top to bottom. */
  tips: number[];
  /** The largest x. */
  maxX: number;
  /** The style actually drawn; a phylogram falls back when no lengths exist. */
  style: TreeStyle;
  /** Branches with no length, drawn as zero in a phylogram. */
  missingLengths: number;
}

export interface TreeStats {
  tips: number;
  internal: number;
  /** Sum of all branch lengths. */
  totalLength: number;
  /** Largest root-to-tip distance. */
  height: number;
  /** Every tip the same distance from the root (a molecular-clock tree). */
  ultrametric: boolean;
  /** Children of the root. */
  rootDegree: number;
  hasLengths: boolean;
}

function leafCountOf(node: NewickNode): number {
  return node.children.length === 0
    ? 1
    : node.children.reduce((sum, child) => sum + leafCountOf(child), 0);
}

/** The tree with each node's children ordered by clade size, or as given. */
export function orderedTree(node: NewickNode, order: CladeOrder): NewickNode {
  if (order === "given" || node.children.length === 0) {
    return node;
  }
  const kids = node.children.map((child) => ({
    child: orderedTree(child, order),
    size: leafCountOf(child),
  }));
  const sign = order === "ascending" ? 1 : -1;
  kids.sort((a, b) => sign * (a.size - b.size));
  return { ...node, children: kids.map((kid) => kid.child) };
}

export function treeStats(root: NewickNode): TreeStats {
  let tips = 0;
  let internal = 0;
  let total = 0;
  let any = false;
  const distances: number[] = [];
  const walk = (node: NewickNode, distance: number, isRoot: boolean) => {
    if (node.length !== null) {
      total += node.length;
      any = true;
    }
    const here = isRoot ? 0 : distance + (node.length ?? 0);
    if (node.children.length === 0) {
      tips += 1;
      distances.push(here);
      return;
    }
    internal += 1;
    for (const child of node.children) {
      walk(child, here, false);
    }
  };
  walk(root, 0, true);
  const height = Math.max(0, ...distances);
  const tolerance = 1e-9 * Math.max(1, height);
  return {
    tips,
    internal,
    totalLength: total,
    height,
    ultrametric:
      distances.length > 1 &&
      distances.every((d) => Math.abs(d - height) <= tolerance),
    rootDegree: root.children.length,
    hasLengths: any,
  };
}

export function layoutTree(
  tree: NewickNode,
  wanted: TreeStyle,
  order: CladeOrder
): TreeLayout {
  const root = orderedTree(tree, order);
  const stats = treeStats(root);
  const style: TreeStyle =
    wanted === "phylogram" && stats.hasLengths && stats.height > 0
      ? "phylogram"
      : "cladogram";
  const nodes: LaidOutNode[] = [];
  const tips: number[] = [];
  let missing = 0;
  const heightOf = new Map<NewickNode, number>();
  const height = (node: NewickNode): number => {
    const known = heightOf.get(node);
    if (known !== undefined) {
      return known;
    }
    const value =
      node.children.length === 0
        ? 0
        : 1 + Math.max(...node.children.map(height));
    heightOf.set(node, value);
    return value;
  };
  const rootHeight = height(root);
  const place = (
    node: NewickNode,
    parent: number | null,
    depth: number,
    distance: number
  ): number => {
    const id = nodes.length;
    if (node.length === null && parent !== null) {
      missing += 1;
    }
    const here =
      parent === null ? (node.length ?? 0) : distance + (node.length ?? 0);
    const entry: LaidOutNode = {
      id,
      parent,
      depth,
      name: node.name,
      length: node.length,
      support: node.support,
      isLeaf: node.children.length === 0,
      leafCount: 0,
      x: style === "phylogram" ? here : rootHeight - height(node),
      y: 0,
      children: [],
    };
    nodes.push(entry);
    if (entry.isLeaf) {
      entry.y = tips.length;
      entry.leafCount = 1;
      tips.push(id);
      return id;
    }
    for (const child of node.children) {
      const childId = place(child, id, depth + 1, here);
      entry.children.push(childId);
      entry.leafCount += nodes[childId].leafCount;
    }
    const first = nodes[entry.children[0]];
    const last = nodes[entry.children.at(-1) ?? 0];
    entry.y = (first.y + last.y) / 2;
    return id;
  };
  place(root, null, 0, 0);
  return {
    nodes,
    tips,
    maxX: Math.max(0, ...nodes.map((node) => node.x)),
    style,
    missingLengths: style === "phylogram" ? missing : 0,
  };
}
